import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import InputMask from 'react-input-mask';

import { FieldLabel } from './../index';

export type FieldMaskInputFormatChars = { [key: string]: string };

type Props = {
  readonly as?: React.ElementType<any>;
  readonly label: string | React.ReactNode;
  readonly mask: string;
  readonly maskChar?: string | null;
  readonly formatChars?: FieldMaskInputFormatChars;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
} & FieldAttributes<any>;

const FieldMaskInput = ({
  as = 'div',
  name,
  label,
  helpText,
  mask,
  maskChar = '_',
  formatChars,
  disabled = false,
  placeholder = '',
  required = false,
}: Props) => {
  const [{ value, onChange, onBlur }, { touched, error, initialValue }] = useField<string>(name);
  const { setFieldValue, setFieldTouched, submitCount } = useFormikContext<any>();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(name, e.target.value, true);
      setFieldTouched(name, true);
    },
    [name, setFieldValue, setFieldTouched],
  );

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  const changed = value !== initialValue;

  const errorText =
    error == null
      ? ''
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && !Array.isArray(error)
          ? Object.values(error)
              .flatMap((v) => (typeof v === 'string' ? [v] : []))
              .join(', ') || JSON.stringify(error)
          : String(error);

  return useMemo(
    () => (
      <Form.Group as={as} controlId={name} className="mb-2">
        {label && (
          <FieldLabel
            text={label}
            helpText={helpText}
            changed={changed}
            required={required}
          />
        )}
        <InputMask
          mask={mask}
          maskChar={maskChar}
          formatChars={formatChars}
          name={name}
          value={value ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          className={`form-control ${touched && error ? 'is-invalid' : ''}`}
          style={required && !disabled ? { backgroundColor: 'lightYellow' } : undefined}
        />
        {(touched || submitCount > 0) && errorText && (
          <Form.Control.Feedback type="invalid">{errorText}</Form.Control.Feedback>
        )}
      </Form.Group>
    ),
    [
      as,
      name,
      label,
      helpText,
      mask,
      maskChar,
      formatChars,
      value,
      changed,
      required,
      disabled,
      placeholder,
      touched,
      error,
      errorText,
      submitCount,
      handleChange,
      handleBlur,
    ],
  );
};

export default FieldMaskInput;
